import React, { useContext, useEffect, useMemo, useState } from "react";
import { Handle, MarkerType, useReactFlow } from "react-flow-renderer";
import { Button, Form, Modal } from "react-bootstrap";
import { NodesContext } from "../provider/IvrContext";
import { toast } from "react-toastify";
import { getApiCall } from "../../../../../service/httpCall";
import {
  API_AGENTS_LIST,
  API_AGENT_GROUPS_LIST,
} from "../../../../../service/url";

const FULL_DAY_MINUTES = 1439;

const convertToMinutes = (time) => {
  if (!time || !time.includes(":")) return 0;
  const [h, m] = time.split(":").map(Number);
  return h * 60 + m;
};

const computeCoverage = (slots) =>
  slots.reduce((acc, s) => {
    if (!s.startTime || !s.endTime) return acc;
    const st = convertToMinutes(s.startTime);
    const en = convertToMinutes(s.endTime);
    if (en > st) acc += en - st;
    return acc;
  }, 0);

const genId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 5);

const TransferNode = ({ data, id, xPos, yPos }) => {
  const { updateNodeData, onRemove } = useContext(NodesContext);
  const { setNodes, setEdges, getNode, getEdges } = useReactFlow();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [groupList, setGroupList] = useState([]);
  const [agentList, setAgentList] = useState([]);

  const [timeSlots, setTimeSlots] = useState(
    data.timeSlots?.length
      ? data.timeSlots.map((slot) => ({
          startTime: slot.start_time || slot.startTime || "",
          endTime: slot.end_time || slot.endTime || "",
          type: slot.type?.replace("transfer_", "") || "",
          transferId: slot.id || "",
        }))
      : [
          {
            startTime: "",
            endTime: "",
            type: "",
            transferId: "",
          },
        ]
  );

  const coverageMins = useMemo(() => computeCoverage(timeSlots), [timeSlots]);
  const isFullDay = coverageMins >= FULL_DAY_MINUTES;

  const savedSlots = data.timeSlots || [];
  const savedCoverage = useMemo(
    () =>
      computeCoverage(
        savedSlots.map((s) => ({
          startTime: s.start_time || s.startTime || "",
          endTime: s.end_time || s.endTime || "",
        }))
      ),
    [savedSlots]
  );
  const savedFullDay = savedCoverage >= FULL_DAY_MINUTES;

  const addTimeSlot = () => {
    setTimeSlots((prev) => [
      ...prev,
      { startTime: "", endTime: "", type: "", transferId: "" },
    ]);
  };

  const removeTimeSlot = (index) => {
    setTimeSlots((prev) => prev.filter((_, i) => i !== index));
  };

  const updateTimeSlot = (index, key, value) => {
    const updated = [...timeSlots];
    updated[index][key] = value;
    if (key === "type") updated[index].transferId = "";
    setTimeSlots(updated);
  };

  const isTimeOverlapping = (currentIndex, start, end) => {
    if (!start || !end) return false;
    const startMinutes = convertToMinutes(start);
    const endMinutes = convertToMinutes(end);
    return timeSlots.some((slot, index) => {
      if (index === currentIndex) return false;
      if (!slot.startTime || !slot.endTime) return false;
      const slotStart = convertToMinutes(slot.startTime);
      const slotEnd = convertToMinutes(slot.endTime);
      return startMinutes < slotEnd && endMinutes > slotStart;
    });
  };

  useEffect(() => {
    getApiCall(API_AGENT_GROUPS_LIST).then(setGroupList).catch(console.log);
    getApiCall(API_AGENTS_LIST).then(setAgentList).catch(console.log);
  }, [data.userId]);

  const attachDefaultNode = () => {
    const existingId = data.defaultNodeId;
    if (existingId && getNode(existingId)) return existingId;

    const newId = genId();
    const parentNode = getNode(id);
    const basePos = parentNode?.position || { x: xPos || 0, y: yPos || 0 };

    setNodes((nds) => [
      ...nds,
      {
        id: newId,
        type: "defaultNode",
        position: { x: (basePos.x || 0) + 120, y: (basePos.y || 0) + 120 },
        data: {
          label: "default Node",
          verb: "default",
          userId: data.userId,
          parent: data.label,
          parentNodeId: id,
        },
      },
    ]);

    setEdges((eds) => [
      ...eds,
      {
        id: `e-${id}-${newId}`,
        source: id,
        target: newId,
        type: "custom",
        deletable: false,
        selectable: false,
        focusable: false,
        markerEnd: { type: MarkerType.Arrow },
        data: { locked: true, label: "default" },
      },
    ]);

    updateNodeData(id, { defaultNodeId: newId });
    return newId;
  };

  const detachDefaultNode = () => {
    const existingId = data.defaultNodeId;
    if (!existingId) return;
    setEdges((eds) => eds.filter((e) => !(e.source === id && e.target === existingId)));
    setNodes((nds) => nds.filter((n) => n.id !== existingId));
    updateNodeData(id, { defaultNodeId: null });
  };

  useEffect(() => {
    const existingId = data.defaultNodeId;
    if (savedFullDay && existingId) {
      detachDefaultNode();
    }
  }, [savedFullDay, data.defaultNodeId]);

  const handleSave = () => {
    for (const slot of timeSlots) {
      if (!slot.startTime || !slot.endTime || !slot.type || !slot.transferId) {
        toast.error("Please fill all fields");
        return;
      }
    }

    const fullDay = coverageMins >= FULL_DAY_MINUTES;

    const finalData = timeSlots.map((slot) => {
      const sourceList = slot.type === "group" ? groupList : agentList;
      const selected = sourceList.find(
        (x) => x.id.toString() === slot.transferId.toString()
      );
      return {
        start_time: slot.startTime,
        end_time: slot.endTime,
        type: `transfer_${slot.type}`,
        id: selected?.id,
        name:
          slot.type === "group" ? selected?.group_name : selected?.agent_name,
      };
    });

    updateNodeData(id, { timeSlots: finalData });

    if (fullDay) {
      detachDefaultNode();
    } else {
      attachDefaultNode();
    }

    toast.success("Details updated successfully.");
    setModalIsOpen(false);
  };

  const handleRemove = () => {
    detachDefaultNode();
    onRemove(id);
  };

  return (
    <div style={{ position: "relative", width: "80px", height: "40px" }}>
      <div
        className="ivr-flow-node"
        style={{
          position: "absolute",
          top: 8,
          left: 0,
          width: "100%",
          height: "100%",
          border: "1.5px solid orange",
          borderRadius: "10px 10px 0 10px",
          background: "#fff",
          zIndex: 3,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "11px",
        }}
      >
        <Handle type="target" position="top" style={{ background: "orange" }} />
        <Handle
          type="source"
          position="bottom"
          style={{ background: "orange", opacity: savedFullDay ? 0 : 1, pointerEvents: "none" }}
          isConnectable={false}
        />

        <span
          className="ivr-flow-node-label"
          style={{
            fontSize: "8px",
            fontWeight: "bold",
            textTransform: "capitalize",
          }}
        >
          {data.label}
        </span>

        <div style={{ display: "flex", gap: "10px" }}>
          <i
            className="fa fa-cog ivr-flow-node-icon"
            style={{ cursor: "pointer", color: "orange" }}
            onClick={() => setModalIsOpen(true)}
          />
          <i
            className="fa fa-trash ivr-flow-node-icon"
            style={{ cursor: "pointer", color: "orange" }}
            onClick={handleRemove}
          />
        </div>
      </div>

      <Modal
        show={modalIsOpen}
        onHide={() => setModalIsOpen(false)}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Transfer</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {timeSlots.map((slot, index) => {
              const overlap = isTimeOverlapping(index, slot.startTime, slot.endTime);
              return (
                <div
                  key={index}
                  style={{
                    border: "1px solid #dddddd65",
                    padding: "12px",
                    borderRadius: "8px",
                    marginBottom: "15px",
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <strong>Time Slot {index + 1}</strong>
                    {timeSlots.length > 1 && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeTimeSlot(index)}
                      >
                        Remove
                      </Button>
                    )}
                  </div>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Start Time <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={slot.startTime}
                      onChange={(e) =>
                        updateTimeSlot(index, "startTime", e.target.value)
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-2">
                    <Form.Label>
                      End Time <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="time"
                      value={slot.endTime}
                      onChange={(e) =>
                        updateTimeSlot(index, "endTime", e.target.value)
                      }
                    />
                  </Form.Group>

                  {overlap && (
                    <div
                      style={{ color: "red", fontSize: "12px", marginBottom: "10px" }}
                    >
                      Time slot overlaps with another slot
                    </div>
                  )}

                  <Form.Group className="mb-2">
                    <Form.Label>
                      Select Type <span style={{ color: "red" }}>*</span>
                    </Form.Label>
                    <Form.Control
                      as="select"
                      value={slot.type}
                      onChange={(e) => updateTimeSlot(index, "type", e.target.value)}
                    >
                      <option value="">Select</option>
                      <option value="group">Call Group</option>
                      <option value="agent">Agent</option>
                    </Form.Control>
                  </Form.Group>

                  {slot.type === "group" && (
                    <Form.Group className="mb-2">
                      <Form.Label>
                        Select Group <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={slot.transferId}
                        onChange={(e) =>
                          updateTimeSlot(index, "transferId", e.target.value)
                        }
                      >
                        <option value="">Select Group</option>
                        {groupList.map((g) => (
                          <option key={g.id} value={g.id}>
                            {g.group_name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  )}

                  {slot.type === "agent" && (
                    <Form.Group className="mb-2">
                      <Form.Label>
                        Select Agent <span style={{ color: "red" }}>*</span>
                      </Form.Label>
                      <Form.Control
                        as="select"
                        value={slot.transferId}
                        onChange={(e) =>
                          updateTimeSlot(index, "transferId", e.target.value)
                        }
                      >
                        <option value="">Select Agent</option>
                        {agentList.map((agent) => (
                          <option key={agent.id} value={agent.id}>
                            {agent.agent_name}
                          </option>
                        ))}
                      </Form.Control>
                    </Form.Group>
                  )}
                </div>
              );
            })}

            <Button variant="success" onClick={addTimeSlot}>
              + Add Time Slot
            </Button>

            <div
              style={{
                marginTop: 20,
                padding: 12,
                background: "#f8f9fa",
                borderRadius: 8,
              }}
            >
              <strong>Day Coverage:</strong> {coverageMins} / {FULL_DAY_MINUTES} mins{" "}
              {isFullDay
                ? "(Full day covered — no default node)"
                : "(Default node will be auto-attached)"}
            </div>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            onClick={handleSave}
            disabled={timeSlots.some(
              (slot, index) =>
                !slot.startTime ||
                !slot.endTime ||
                !slot.type ||
                !slot.transferId ||
                isTimeOverlapping(index, slot.startTime, slot.endTime)
            )}
          >
            Save
          </Button>
          <Button variant="secondary" onClick={() => setModalIsOpen(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default TransferNode;
